import React from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTheme, type ThemeMode } from "@/contexts/ThemeContext";
import { useTelegram } from "@/contexts/UnifiedAuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";

interface ThemeSelectorProps {
  className?: string;
  showCard?: boolean;
}

/**
 * Компонент выбора темы для мобильного
 * Показывает select с тремя опциями: Светлая, Темная, Автоматически
 */
export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  className,
  showCard = true
}) => {
  const { themeMode, setThemeMode, resolvedTheme, isSystemDarkMode } = useTheme();
  const { isTelegram } = useTelegram();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Не показываем в Telegram
  if (isTelegram) {
    return null;
  }

  const themes: Array<{
    value: ThemeMode;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
      {
        value: 'light',
        label: t('theme.light.label'),
        description: t('theme.light.desc'),
        icon: Sun,
      },
      {
        value: 'dark',
        label: t('theme.dark.label'),
        description: t('theme.dark.desc'),
        icon: Moon,
      },
      {
        value: 'auto',
        label: t('theme.auto.label'),
        description: t('theme.auto.desc', {
          theme: resolvedTheme === 'dark' ? t('theme.resolved.dark') : t('theme.resolved.light')
        }),
        icon: Monitor,
      },
    ];

  const currentTheme = themes.find(theme => theme.value === themeMode);

  const selectorContent = (
    <div className={className}>
      <div className="space-y-3">
        <Label htmlFor="theme-selector" className="text-base font-medium">
          {t('theme.label')}
        </Label>

        <Select
          value={themeMode}
          onValueChange={(value: ThemeMode) => setThemeMode(value)}
        >
          <SelectTrigger id="theme-selector" className="w-full">
            <SelectValue>
              <div className="flex items-center gap-2">
                {currentTheme?.icon && (
                  <currentTheme.icon className="h-4 w-4" />
                )}
                {currentTheme?.label}
              </div>
            </SelectValue>
          </SelectTrigger>

          <SelectContent>
            {themes.map(({ value, label, description, icon: Icon }) => (
              <SelectItem key={value} value={value} className="py-3">
                <div className="flex items-start gap-3 w-full">
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {description}
                    </div>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Дополнительная информация */}
        <div className="text-sm text-muted-foreground">
          {themeMode === 'auto' && (
            <div className="flex items-center gap-2">
              <Monitor className="h-3.5 w-3.5" />
              <span>
                {t('theme.system_label', {
                  theme: isSystemDarkMode ? t('theme.resolved.dark') : t('theme.resolved.light')
                })}
              </span>
            </div>
          )}
          {themeMode !== 'auto' && (
            <div className="flex items-center gap-2">
              {currentTheme?.icon && (
                <currentTheme.icon className="h-3.5 w-3.5" />
              )}
              <span>
                {t('theme.active_label', {
                  theme: currentTheme?.label.toLowerCase()
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!showCard) {
    return selectorContent;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sun className="h-5 w-5" />
          {t('theme.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ThemeSelector showCard={false} />
      </CardContent>
    </Card>
  );
};

/**
 * Компактный вариант селектора без карты
 */
export const ThemeSelectorInline: React.FC<{ className?: string }> = ({ className }) => {
  return <ThemeSelector className={className} showCard={false} />;
};

/**
 * Список радио-кнопок для более наглядного выбора темы
 */
export const ThemeRadioGroup: React.FC<{ className?: string }> = ({ className }) => {
  const { themeMode, setThemeMode, resolvedTheme } = useTheme();
  const { isTelegram } = useTelegram();
  const { t } = useTranslation();

  // Не показываем в Telegram
  if (isTelegram) {
    return null;
  }

  const themes: Array<{
    value: ThemeMode;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
      {
        value: 'light',
        label: t('theme.light.label_full'),
        description: t('theme.light.desc_alt'),
        icon: Sun,
      },
      {
        value: 'dark',
        label: t('theme.dark.label_full'),
        description: t('theme.dark.desc_alt'),
        icon: Moon,
      },
      {
        value: 'auto',
        label: t('theme.auto.label'),
        description: t('theme.auto.desc_alt', {
          theme: resolvedTheme === 'dark' ? t('theme.resolved.dark') : t('theme.resolved.light')
        }),
        icon: Monitor,
      },
    ];

  return (
    <div className={className}>
      <div className="space-y-3">
        <Label className="text-base font-medium">{t('theme.label')}</Label>

        <div className="space-y-2">
          {themes.map(({ value, label, description, icon: Icon }) => (
            <div
              key={value}
              className={`
                relative flex items-start space-x-3 rounded-lg border p-3 cursor-pointer transition-colors
                ${themeMode === value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/50'
                }
              `}
              onClick={() => setThemeMode(value)}
            >
              <div className="flex h-5 items-center">
                <input
                  type="radio"
                  name="theme"
                  value={value}
                  checked={themeMode === value}
                  onChange={() => setThemeMode(value)}
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-2 focus:ring-primary"
                />
              </div>

              <Icon className="h-5 w-5 mt-0.5 text-foreground" />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {label}
                </div>
                <div className="text-sm text-muted-foreground">
                  {description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThemeSelector;